import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  Card,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Tabs,
  Tab
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

export default function WebsiteContentManager() {

  const API = process.env.REACT_APP_LIVE_URL;
  const token = localStorage.getItem("token");
  const fileRef = useRef();

  const [tab, setTab] = useState(0);

  const [noticeRows, setNoticeRows] = useState([]);
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [noticeEditing, setNoticeEditing] = useState(null);

  const [message, setMessage] = useState("");
  const [ctaText, setCtaText] = useState("");
  const [ctaLink, setCtaLink] = useState("");
  const [active, setActive] = useState(true);

  const [popupRows, setPopupRows] = useState([]);
  const [popupAdd, setPopupAdd] = useState(false);
  const [popupEditingId, setPopupEditingId] = useState(null);

  const [image, setImage] = useState(null);
  const [imageFile, setImageFile] = useState(null);

  const fetchNotices = async () => {
    try {
      const res = await fetch(
        `${API}/AdminPanel/getWebsiteContents?type=notification`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();

      if (data?.status) {
        setNoticeRows(
          data.data.map(item => ({
            id: item._id,
            message: item.text || "",
            ctaText: item.ctaText || "",
            ctaLink: item.ctaLink || "",
            active: item.isActive ?? false,
            createdAt: item.createdAt
              ? new Date(item.createdAt).toLocaleString()
              : "-"
          }))
        );
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchPopups = async () => {
    try {
      const res = await fetch(
        `${API}/AdminPanel/getWebsiteContents?type=popup`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();

      if (data?.status) {
        setPopupRows(
          data.data.map(item => ({
            id: item._id,
            image: item.image || "",
            status: item.isActive ? "Active" : "Inactive",
            createdAt: item.createdAt
              ? new Date(item.createdAt).toLocaleString()
              : "-"
          }))
        );
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchNotices();
    fetchPopups();
  }, []);

  const handleNoticeSave = async () => {
    if (!message.trim()) return alert("Enter message");

    const formData = new FormData();
    formData.append("type", "notification");
    formData.append("text", message);
    formData.append("ctaText", ctaText);
    formData.append("ctaLink", ctaLink);
    formData.append("isActive", active);

    const url = !noticeEditing
      ? `${API}/AdminPanel/addWebsiteContent`
      : `${API}/AdminPanel/editWebsiteContent/${noticeEditing.id}`;

    const method = !noticeEditing ? "POST" : "PUT";

    await fetch(url, {
      method,
      headers: { Authorization: `Bearer ${token}` },
      body: formData
    });

    resetNoticeForm();
    setNoticeOpen(false);
    fetchNotices();
  };

  const resetNoticeForm = () => {
    setMessage("");
    setCtaText("");
    setCtaLink("");
    setActive(true);
    setNoticeEditing(null);
  };

  const handlePopupSave = async () => {
    if (!imageFile && !popupEditingId)
      return alert("Upload image");

    const formData = new FormData();
    formData.append("type", "popup");
    if (imageFile) formData.append("image", imageFile);

    const url = !popupEditingId
      ? `${API}/AdminPanel/addWebsiteContent`
      : `${API}/AdminPanel/editWebsiteContent/${popupEditingId}`;

    const method = !popupEditingId ? "POST" : "PUT";

    await fetch(url, {
      method,
      headers: { Authorization: `Bearer ${token}` },
      body: formData
    });

    setImage(null);
    setImageFile(null);
    setPopupEditingId(null);
    setPopupAdd(false);
    fetchPopups();
  };

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (f) {
      setImageFile(f);
      setImage(URL.createObjectURL(f));
    }
  };

  const getSnoColumn = () => ({
    field: "sno",
    headerName: "S No",
    width: 80,
    sortable: false,
    renderCell: (params) => {
      const index = params.api
        .getSortedRowIds()
        .findIndex(id => id === params.id);
      return index + 1;
    }
  });

  const noticeColumns = [
    getSnoColumn(),
    { field: "message", headerName: "Flash Notice", flex: 2 },
    { field: "ctaText", headerName: "CTA Text", flex: 1 },
    {
      field: "ctaLink",
      headerName: "CTA Link",
      flex: 1,
      renderCell: (p) =>
        p.value ? (
          <a href={p.value} target="_blank" rel="noreferrer">
            {p.value}
          </a>
        ) : "-"
    },
    {
      field: "active",
      headerName: "Status",
      width: 120,
      renderCell: (p) => (
        <span style={{ color: p.value ? "green" : "red" }}>
          {p.value ? "Active" : "Inactive"}
        </span>
      )
    },
    { field: "createdAt", headerName: "Created At", flex: 1 },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      renderCell: (params) => (
        <Button size="small" onClick={() => {
          setNoticeEditing(params.row);
          setMessage(params.row.message);
          setCtaText(params.row.ctaText);
          setCtaLink(params.row.ctaLink);
          setActive(params.row.active);
          setNoticeOpen(true);
        }}>
          Edit
        </Button>
      )
    }
  ];

  const popupColumns = [
    getSnoColumn(),
    {
      field: "image",
      headerName: "Image",
      width: 150,
      renderCell: (p) =>
        p.value ? (
          <img src={p.value} alt="" style={{ width: 60 }} />
        ) : "No image"
    },
    { field: "status", headerName: "Status", width: 120 },
    { field: "createdAt", headerName: "Created At", flex: 1 },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      renderCell: (params) => (
        <Button size="small" onClick={() => {
          setPopupEditingId(params.row.id);
          setImage(params.row.image);
          setPopupAdd(true);
        }}>
          Edit
        </Button>
      )
    }
  ];

  return (
    <Box p={3}>

      <Typography variant="h4" mb={3}>
        Website Content Manager
      </Typography>

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="Flash Notices" />
        <Tab label="Popup Images" />
      </Tabs>

      {tab === 0 && (
        <>
          <Button variant="contained" onClick={() => setNoticeOpen(true)}>
            Add Notice
          </Button>

          <Card sx={{ height: 520, mt: 2 }}>
            <DataGrid rows={noticeRows} columns={noticeColumns} />
          </Card>

          <Dialog open={noticeOpen} onClose={() => setNoticeOpen(false)} fullWidth maxWidth="sm">
            <DialogTitle>
              {noticeEditing ? "Edit Notice" : "Add Notice"}
            </DialogTitle>
            <DialogContent>
              <TextField
                label="Message"
                fullWidth
                multiline
                rows={3}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                sx={{ mt: 2 }}
              />
              <TextField
                label="CTA Text"
                fullWidth
                value={ctaText}
                onChange={(e) => setCtaText(e.target.value)}
                sx={{ mt: 2 }}
              />
              <TextField
                label="CTA Link"
                fullWidth
                value={ctaLink}
                onChange={(e) => setCtaLink(e.target.value)}
                sx={{ mt: 2 }}
              />
              <FormControlLabel
                sx={{ mt: 2 }}
                control={
                  <Switch
                    checked={active}
                    onChange={(e) => setActive(e.target.checked)}
                  />
                }
                label={active ? "Active" : "Inactive"}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setNoticeOpen(false)}>Cancel</Button>
              <Button variant="contained" onClick={handleNoticeSave}>
                Save
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
      {tab === 1 && (
        <>
          {!popupAdd ? (
            <Button variant="contained" onClick={() => setPopupAdd(true)}>
              Add Popup Image
            </Button>
          ) : (
            <Button variant="outlined" onClick={() => setPopupAdd(false)}>
              Back to List
            </Button>
          )}

          {!popupAdd && (
            <Card sx={{ height: 520, mt: 2 }}>
              <DataGrid rows={popupRows} columns={popupColumns} />
            </Card>
          )}

          {popupAdd && (
            <Card sx={{ p: 3, mt: 2 }}>
              <Button variant="outlined" onClick={() => fileRef.current.click()}>
                {image ? "Change Image" : "Upload Image"}
              </Button>

              <input
                ref={fileRef}
                hidden
                type="file"
                accept="image/*"
                onChange={handleFile}
              />

              {image && (
                <Box mt={2}>
                  <img src={image} alt="" style={{ width: 200 }} />
                </Box>
              )}

              <Button
                variant="contained"
                fullWidth
                sx={{ mt: 3 }}
                onClick={handlePopupSave}
              >
                {popupEditingId ? "Update" : "Add"}
              </Button>
            </Card>
          )}
        </>
      )}

    </Box>
  );
}